
import Vue from "vue";
import AppSubheader from "@/shared/components/AppSubheader.vue";
import DetailsPane, { DetailsPaneObjectType } from "@/features/Supplier/DetailsPane.vue";
import ScorecardList from "@/features/Scores/ScorecardList.vue";
import IncidentList from "@/features/Incidents/IncidentList.vue";
import SupplierEventList from "@/features/SupplierEvents/SupplierEventList.vue";
import AppError from "@/shared/components/AppError.vue";
import TimelineGraph from "@/features/Timeline/TimelineGraph.vue";
import Supplier from "@/features/Supplier/Supplier";
import { useUserStore } from "@/plugins/pinia";
import { mapStores } from "pinia";
import TitledSubsection from "@/shared/components/TitledSubsection.vue";
import ScrollablePane from "@/shared/components/ScrollablePane.vue";
import { DataTableOptions } from "@/shared/models/PagingParameter";

const maxSuppliersToReturn = 10;

export default Vue.extend({
    name: "SupplierPage",
    components: {
        AppSubheader,
        ScorecardList,
        IncidentList,
        SupplierEventList,
        TimelineGraph,
        AppError,
        TitledSubsection,
        ScrollablePane,
        DetailsPane,
    },
    data: () => ({
        direction: "right",
        breadCrumbItems: [
            {
                label: true,
                text: "Suppliers",
                disabled: true,
                href: "",
            },
            {
                id: "subheaderLoader",
                label: false,
                loading: true,
            },
            {
                id: "latestScore",
                label: false,
                loading: true,
            },
        ],
        supplier: null as Supplier | null,
        suppliers: null as Supplier[] | null,
        detailsPaneObjectTypes: DetailsPaneObjectType,
        currentDetailsPaneObjectType: null as string | null, // a DetailsPaneObjectType
        showDetailsPane: false,
        selectedScoreID: null as string | null,
        selectedScorecardID: null as string | null,
        selectedIncidentID: null as string | null,
        selectedSupplierEventID: null as string | null,
        selectedSupplierID: null as string | null,
        isForNewObject: false,
        latestScore: null as number | null,
        percentChange: null as number | null,
        errorMessage: "",
        showError: false,
        loading: false,
        beforeDate: new Date().toISOString(),
        afterDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(), // one year ago today
        incidentListOptions: {
            sortBy: ["id"],
            sortDesc: [false],
            itemsPerPage: 10,
            page: 1,
        } as DataTableOptions,
        scorecardListOptions: {
            sortBy: ["id"],
            sortDesc: [false],
            itemsPerPage: 10,
            page: 1,
        } as DataTableOptions,
        supplierEventListOptions: {
            sortBy: ["createdTimestampUTC"],
            sortDesc: [true],
            itemsPerPage: 10,
            page: 1,
        } as DataTableOptions,
        timelineKey: 0,
        scorecardListKey: 0,
        incidentListKey: 0,
        supplierEventListKey: 0,
        supplierQuery: "",
        loadingSearch: false,
        searchTimeoutID: 0,
    }),
    computed: {
        ...mapStores(useUserStore),
        currentReferrerURL(): string {
            return "https://" + this.userStore.referrer + '/#supplierdetail/%7B"id"%3A"' + this.selectedSupplierID + '"%7D';
        },
        canCreateScorecards(): boolean {
            if (!this.supplier) {
                return false;
            }

            return this.$scoreAuthorizationService.canCreateScorecard(this.supplier.id);
        },
        canCreateIncidents(): boolean {
            if (!this.supplier) {
                return false;
            }

            return this.$incidentAuthorizationService.canCreateIncident(this.supplier.id);
        },
        canCreateEvents(): boolean {
            if (!this.supplier) {
                return false;
            }

            // Permission to create a supplier event is already covered by permission to view the page for this supplier at all, so no
            // extra permission check is needed here

            return true;
        },
        scorecardID(): string | null {
            return this.$route.query.scorecardID ? (this.$route.query.scorecardID as string) : null;
        },
        incidentID(): string | null {
            return this.$route.query.incidentID ? (this.$route.query.incidentID as string) : null;
        },
        supplierEventID(): string | null {
            return this.$route.query.supplierEventID ? (this.$route.query.supplierEventID as string) : null;
        },
        oneYearAgoToday(): Date {
            return new Date(new Date().setFullYear(new Date().getFullYear() - 1));
        },
    },
    created: async function () {
        window.addEventListener("popstate", this.onBrowserNav); // Enables browser forward/back without full page reload

        // If there are more than 1 supplier available, always show the dropdown.
        await this.getSuppliers(this.supplierQuery);

        this.getSortFromParams();

        var supplierIDFromLocalStorage = localStorage.getItem("supplierID");
        // If we have an id attached, we want the specific supplier.
        if (this.$route.params.supplierID) {
            this.selectedSupplierID = this.$route.params.supplierID;

            // Add the current supplier to the dropdown if it isn't already there
            if (!this.suppliers?.map(s => s.id).includes(this.selectedSupplierID)) {
                await this.getSupplierData(this.selectedSupplierID);
                this.suppliers?.push(this.supplier!);
                this.suppliers?.sort((a, b) => a.name.localeCompare(b.name));
            }
        }
        // Otherwise we've gotten here from the supplier portal left nav button and want to open the last open supplier, if possible
        else if (supplierIDFromLocalStorage != null && this.suppliers?.find(supplier => supplier.id == supplierIDFromLocalStorage)) {
            this.selectedSupplierID = supplierIDFromLocalStorage;

            //push the id we get from local storage into the URL
            this.$router.replace({ name: "supplier", params: { supplierID: supplierIDFromLocalStorage } });
        }

        // Fetch Supplier if we have the ID
        if (this.selectedSupplierID) {
            await this.getSupplierData(this.selectedSupplierID);
        }

        // If we have an ID for a details object, open the details pane
        if (this.scorecardID) {
            this.selectedScorecardID = this.scorecardID as string;
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.Scorecard;
            this.showDetailsPane = true;
        } else if (this.incidentID) {
            this.selectedIncidentID = this.incidentID as string;
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.Incident;
            this.showDetailsPane = true;
        } else if (this.supplierEventID) {
            this.selectedIncidentID = this.supplierEventID as string;
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.SupplierEvent;
            this.showDetailsPane = true;
        }
    },
    destroyed: function () {
        window.removeEventListener("popstate", this.onBrowserNav);
    },
    watch: {
        scorecardID(newValue: string | null) {
            if (newValue) {
                this.openScorecardInDetailsPane(newValue);
            } else {
                this.closeDetailsPane();
            }
        },
        incidentID(newValue: string | null) {
            if (newValue) {
                this.openIncidentInDetailsPane(newValue);
            } else {
                this.closeDetailsPane();
            }
        },
        supplierEventID(newValue: string | null) {
            if (newValue) {
                this.openSupplierEventInDetailsPane(newValue);
            } else {
                this.closeDetailsPane();
            }
        },
        supplierQuery(val: string) {
            if (this.suppliers?.length == 1 && this.suppliers[0].name == val) {
                return;
            }
            const DEBOUNCE_TIME = 200;
            window.clearTimeout(this.searchTimeoutID);
            this.searchTimeoutID = window.setTimeout(async () => {
                await this.getSuppliers(val);
            }, DEBOUNCE_TIME);
        },
    },
    methods: {
        getSortFromParams() {
            // Set date range from Query params
            this.beforeDate = this.$route.query.beforeDate ? String(this.$route.query.beforeDate) : new Date().toISOString();
            this.afterDate = this.$route.query.afterDate ? String(this.$route.query.afterDate) : this.oneYearAgoToday.toISOString();

            //Set incident list options from Query params
            this.incidentListOptions.sortBy = [(this.$route.query.sortIncidentsBy as string) || localStorage.getItem("sortIncidentsBy") || "id"];
            this.incidentListOptions.sortDesc = [(this.$route.query.sortIncidentsDesc as string) === "true" || localStorage.getItem("sortIncidentsDesc") === "true" || false];
            this.incidentListOptions.itemsPerPage = Number(this.$route.query.incidentLimit) || Number(localStorage.getItem("incidentLimit")) || 10;
            this.incidentListOptions.page = Number(this.$route.query.incidentPage) || Number(localStorage.getItem("incidentPage")) || 1;

            //Set scorecard list options from Query params
            this.scorecardListOptions.sortBy = [(this.$route.query.sortScorecardsBy as string) || localStorage.getItem("sortScorecardsBy") || "id"];
            this.scorecardListOptions.sortDesc = [(this.$route.query.sortScorecardsDesc as string) === "true" || localStorage.getItem("sortScorecardsDesc") === "true" || false];
            this.scorecardListOptions.itemsPerPage = Number(this.$route.query.scorecardLimit) || Number(localStorage.getItem("scorecardLimit")) || 10;
            this.scorecardListOptions.page = Number(this.$route.query.scorecardPage) || Number(localStorage.getItem("scorecardPage")) || 1;

            //Set event list options from Query params
            this.supplierEventListOptions.sortBy = [(this.$route.query.sortSupplierEventsBy as string) || localStorage.getItem("sortSupplierEventsBy") || "id"];
            this.supplierEventListOptions.sortDesc = [(this.$route.query.sortSupplierEventsDesc as string) === "true" || localStorage.getItem("sortSupplierEventsDesc") === "true" || false];
            this.supplierEventListOptions.itemsPerPage = Number(this.$route.query.supplierEventLimit) || Number(localStorage.getItem("supplierEventLimit")) || 10;
            this.supplierEventListOptions.page = Number(this.$route.query.supplierEventPage) || Number(localStorage.getItem("supplierEventPage")) || 1;
        },
        getSuppliers: async function (query: string) {
            try {
                this.loadingSearch = true;
                this.suppliers = await this.$supplierService.getSuppliersFromServer(query, maxSuppliersToReturn);

                //Sort the supplier list by name
                this.suppliers.sort((a, b) => a.name.localeCompare(b.name));
            } catch (e) {
                this.errorMessage = "Failed to fetch suppliers for user";
                this.showError = true;
                console.error(e);
            } finally {
                this.loadingSearch = false;
            }
        },
        getSupplierData: async function (id: string) {
            try {
                this.loading = true;
                const supplierPromise = this.$supplierService.getSupplierFromServer(id);
                const scorePromise = this.getLatestScoreAndChange(id);
                this.supplier = await supplierPromise;
                await scorePromise;
                localStorage.setItem("supplierID", `${this.supplier?.id}`);
            } catch (e) {
                this.errorMessage = "Failed to fetch supplier data";
                this.showError = true;
                console.error(e);
            } finally {
                // Once we've gotten back data
                this.breadCrumbItems.find(item => item.id == "subheaderLoader")!.loading = false;
                this.loading = false;
            }
        },
        getLatestScoreAndChange: async function (id: string) {
            const scoreAndChange = await this.$scoreService.getLatestScoreAndPercentChange(id);
            this.latestScore = Math.round(scoreAndChange.percentageLatestScore! * 100);
            this.percentChange = Math.round(scoreAndChange.percentageDifference! * 100);
        },
        createScorecard: async function () {
            var newScorecardID = "";
            try {
                // Create a new scorecard and pass in the supplier ID
                newScorecardID = await this.$scoreService.createScorecard(this.selectedSupplierID!);
                this.$router.push({
                    name: "supplier",
                    query: {
                        scorecardID: newScorecardID ?? "",
                    },
                });
                this.selectedScorecardID = newScorecardID;
                this.currentDetailsPaneObjectType = DetailsPaneObjectType.Scorecard;
                this.isForNewObject = true;
                this.showDetailsPane = true;
            } catch (e) {
                this.errorMessage = "Failed to create new scorecard";
                this.showError = true;
                console.error(e);
            }
        },
        createIncident: async function () {
            var newIncidentID = "";
            try {
                // Create a new incident and pass in the supplier ID
                newIncidentID = await this.$incidentService.createIncident(this.selectedSupplierID!);
                this.$router.push({
                    name: "supplier",
                    query: {
                        incidentID: newIncidentID ?? "",
                    },
                });
                this.selectedIncidentID = newIncidentID;
                this.currentDetailsPaneObjectType = DetailsPaneObjectType.Incident;
                this.isForNewObject = true;
                this.showDetailsPane = true;
            } catch (e) {
                this.errorMessage = "Failed to create new incident";
                this.showError = true;
                console.error(e);
            }
        },
        createSupplierEvent: async function () {
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.SupplierEvent;
            this.showDetailsPane = true;
        },
        onSupplierSelected: async function (supplierID: string) {
            if (supplierID) {
                // Fetch Supplier
                try {
                    console.log("Fetching supplier");
                    await this.getSupplierData(supplierID);
                    localStorage.setItem("supplierID", `${this.supplier?.id}`);

                    //Update route with the supplier we selected.
                    this.$router.push({ name: "supplier", params: { supplierID: supplierID } });

                    //This solves the problem where selecting a new supplier after updating sorts, does not reset the sort even though the URL is updated by the Push
                    //This will reset the sorts to the defaults
                    //TODO: The one problem here is that @update:options is triggered, which updates then emits and is caught in
                    //@incident-sort-updated and @scorecard-sort-updated which both push the new (default) params into the history/route (so it's pushed 2 times on top of the original push).
                    //This problem is worse than it seems, because when you try to go back, it will also trigger a similar push because @incident-sort-updated and @scorecard-sort-updated
                    //are triggered again. So you can actually never get back in history.
                    //TODO: Probably applies to the event table as well.
                    this.getSortFromParams();
                } catch (e) {
                    this.errorMessage = "Failed to fetch supplier data";
                    this.showError = true;
                    console.error(e);
                } finally {
                    this.breadCrumbItems.find(item => item.id == "subheaderLoader")!.loading = false;
                }
            }
        },
        onDetailsClosed() {
            this.closeDetailsPane();
            this.removeIDsFromQuery();
        },
        closeDetailsPane() {
            this.showDetailsPane = false;
            this.selectedScoreID = null;
            this.selectedScorecardID = null;
            this.selectedIncidentID = null;
            this.selectedSupplierEventID = null;
            this.currentDetailsPaneObjectType = null;
        },
        removeIDsFromQuery() {
            // Remove the details object's ID from the route's query
            const query = Object.keys(this.$route.query)
                .filter(key => !["scorecardID", "incidentID", "supplierEventID"].includes(key))
                .reduce((obj, key) => ({ ...obj, [key]: this.$route.query[key] }), {});
            const params = Object.assign({}, this.selectedSupplierID ? { supplierID: this.selectedSupplierID } : null);
            this.$router.push({ name: "supplier", params: params, query: query });
        },
        onScorecardUpdated() {
            this.timelineKey += 1;
            this.scorecardListKey += 1;
            this.isForNewObject = false;

            this.getLatestScoreAndChange(this.selectedSupplierID!);
        },
        onIncidentUpdated() {
            this.timelineKey += 1;
            this.incidentListKey += 1;
            this.isForNewObject = false;
        },
        onSupplierEventUpdated() {
            this.timelineKey += 1;
            this.supplierEventListKey += 1;
            this.isForNewObject = false;
        },
        onScorecardListItemClicked(scorecardID: string) {
            this.openScorecardInDetailsPane(scorecardID);
            this.addScorecardIDToQuery(scorecardID);
        },
        onIncidentListItemClicked(incidentID: string) {
            this.openIncidentInDetailsPane(incidentID);
            this.addIncidentIDToQuery(incidentID);
        },
        onSupplierEventListItemClicked(supplierEventID: string) {
            this.openSupplierEventInDetailsPane(supplierEventID);
            this.addSupplierEventIDToQuery(supplierEventID);
        },
        openScorecardInDetailsPane(scorecardID: string): void {
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.Scorecard;
            this.selectedScorecardID = scorecardID;
            this.showDetailsPane = true;
        },
        openIncidentInDetailsPane(incidentID: string): void {
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.Incident;
            this.selectedIncidentID = incidentID;
            this.showDetailsPane = true;
        },
        openSupplierEventInDetailsPane(supplierEventID: string): void {
            this.currentDetailsPaneObjectType = DetailsPaneObjectType.SupplierEvent;
            this.selectedSupplierEventID = supplierEventID;
            this.showDetailsPane = true;
        },
        addScorecardIDToQuery(scorecardID: string): void {
            const params = Object.assign({}, this.selectedSupplierID ? { supplierID: this.selectedSupplierID } : null);
            this.$router.push({ name: "supplier", params: params, query: { ...this.$route.query, scorecardID } });
        },
        addIncidentIDToQuery(incidentID: string): void {
            const params = Object.assign({}, this.selectedSupplierID ? { supplierID: this.selectedSupplierID } : null);
            this.$router.push({ name: "supplier", params: params, query: { ...this.$route.query, incidentID } });
        },
        addSupplierEventIDToQuery(supplierEventID: string): void {
            const params = Object.assign({}, this.selectedSupplierID ? { supplierID: this.selectedSupplierID } : null);
            this.$router.push({ name: "supplier", params: params, query: { ...this.$route.query, supplierEventID } });
        },
        onUpdateDateRange(afterDate: string, beforeDate: string) {
            this.afterDate = afterDate;
            this.beforeDate = beforeDate;
            this.UpdateQueryParams();
        },
        async onBrowserNav(event: PopStateEvent) {
            // Restore state
            this.selectedSupplierID = event.state.supplierID as string;
            this.incidentListOptions = {
                sortBy: [event.state.sortIncidentsBy as string],
                sortDesc: [(event.state.sortIncidentsDesc as string) === "true"],
                itemsPerPage: event.state.incidentLimit as number,
                page: event.state.incidentPage as number,
            } as DataTableOptions;
            this.scorecardListOptions = {
                sortBy: [event.state.sortScorecardsBy as string],
                sortDesc: [(event.state.sortScorecardsDesc as string) === "true"],
                itemsPerPage: event.state.scorecardLimit as number,
                page: event.state.scorecardPage as number,
            } as DataTableOptions;
            this.supplierEventListOptions = {
                sortBy: [event.state.sortSupplierEventsBy as string],
                sortDesc: [(event.state.sortSupplierEventsDesc as string) === "true"],
                itemsPerPage: event.state.supplierEventLimit as number,
                page: event.state.supplierEventPage as number,
            } as DataTableOptions;

            this.$route.query.scorecardID = event.state.scorecardID;
            this.$route.query.incidentID = event.state.incidentID;
            this.$route.query.supplierEventID = event.state.supplierEventID;

            if (this.afterDate != event.state.afterDate || this.beforeDate != event.state.beforeDate) {
                this.afterDate = event.state.afterDate ? (event.state.afterDate as string) : this.oneYearAgoToday.toISOString();
                this.beforeDate = event.state.beforeDate ? (event.state.beforeDate as string) : new Date().toISOString();
                this.timelineKey++;
            }
        },
        async handleChangeIncidentTableOptions(newOptions: DataTableOptions) {
            // Only update if the options were actually changed
            if (
                newOptions.sortBy[0] != this.incidentListOptions.sortBy[0] ||
                newOptions.sortDesc[0] != this.incidentListOptions.sortDesc[0] ||
                newOptions.itemsPerPage != this.incidentListOptions.itemsPerPage ||
                newOptions.page != this.incidentListOptions.page
            ) {
                this.incidentListOptions = newOptions;

                this.UpdateQueryParams();
            }
        },
        async handleChangeScorecardTableOptions(newOptions: DataTableOptions) {
            // Only update if the options were actually changed
            if (
                newOptions.sortBy[0] != this.scorecardListOptions.sortBy[0] ||
                newOptions.sortDesc[0] != this.scorecardListOptions.sortDesc[0] ||
                newOptions.itemsPerPage != this.scorecardListOptions.itemsPerPage ||
                newOptions.page != this.scorecardListOptions.page
            ) {
                this.scorecardListOptions = newOptions;

                this.UpdateQueryParams();
            }
        },
        async handleChangeSupplierEventTableOptions(newOptions: DataTableOptions) {
            // Only update if the options were actually changed
            if (
                newOptions.sortBy[0] != this.supplierEventListOptions.sortBy[0] ||
                newOptions.sortDesc[0] != this.supplierEventListOptions.sortDesc[0] ||
                newOptions.itemsPerPage != this.supplierEventListOptions.itemsPerPage ||
                newOptions.page != this.supplierEventListOptions.page
            ) {
                this.supplierEventListOptions = newOptions;

                this.UpdateQueryParams();
            }
        },
        UpdateQueryParams() {
            const state = {
                sortScorecardsBy: String(this.scorecardListOptions.sortBy),
                sortScorecardsDesc: String(this.scorecardListOptions.sortDesc),
                scorecardLimit: this.scorecardListOptions.itemsPerPage,
                scorecardPage: this.scorecardListOptions.page,
                sortIncidentsBy: String(this.incidentListOptions.sortBy),
                sortIncidentsDesc: String(this.incidentListOptions.sortDesc),
                incidentLimit: this.incidentListOptions.itemsPerPage,
                incidentPage: this.incidentListOptions.page,
                sortSupplierEventsBy: String(this.supplierEventListOptions.sortBy),
                sortSupplierEventsDesc: String(this.supplierEventListOptions.sortDesc),
                supplierEventLimit: this.supplierEventListOptions.itemsPerPage,
                supplierEventPage: this.supplierEventListOptions.page,
                beforeDate: this.beforeDate,
                afterDate: this.afterDate,
                supplierID: this.selectedSupplierID,

                // add key-value pairs for the details object IDs, if any are non-null
                ...Object.assign(
                    {},
                    this.scorecardID ? { scorecardID: this.scorecardID } : null,
                    this.incidentID ? { incidentID: this.incidentID } : null,
                    this.supplierEventID ? { supplierEventID: this.supplierEventID } : null
                ),
            };

            const stringQueryParams = new URLSearchParams({
                sortScorecardsBy: String(this.scorecardListOptions.sortBy),
                sortScorecardsDesc: String(this.scorecardListOptions.sortDesc),
                scorecardLimit: String(this.scorecardListOptions.itemsPerPage),
                scorecardPage: String(this.scorecardListOptions.page),
                sortIncidentsBy: String(this.incidentListOptions.sortBy),
                sortIncidentsDesc: String(this.incidentListOptions.sortDesc),
                incidentLimit: String(this.incidentListOptions.itemsPerPage),
                incidentPage: String(this.incidentListOptions.page),
                sortSupplierEventsBy: String(this.supplierEventListOptions.sortBy),
                sortSupplierEventsDesc: String(this.supplierEventListOptions.sortDesc),
                supplierEventLimit: String(this.supplierEventListOptions.itemsPerPage),
                supplierEventPage: String(this.supplierEventListOptions.page),
                beforeDate: String(this.beforeDate),
                afterDate: String(this.afterDate),
                supplierID: String(this.selectedSupplierID),

                // add key-value pairs for the details object IDs, if any are non-null
                ...Object.assign(
                    {},
                    this.scorecardID ? { scorecardID: this.scorecardID } : null,
                    this.incidentID ? { incidentID: this.incidentID } : null,
                    this.supplierEventID ? { supplierEventID: this.supplierEventID } : null
                ),
            });

            // Set sort params to local storage
            for (const [key, value] of Object.entries(state)) {
                // Setting supplierID is handled elsewhere
                if (key !== "supplierID") {
                    localStorage.setItem(key, String(value));
                }
            }

            history.pushState(state, "", window.location.href.split("?")[0] + "?" + stringQueryParams.toString());
        },
    },
});
